import {
  Badge,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui";
import { cn } from "@/lib/utils";
import { setCommunityData } from "@/redux/slices";
import { routes } from "@/routes/utils";
import {
  useDeleteCommunityMutation,
  useGetCommunities,
  useGetCommunityDeleteState,
  useGoLiveCommunityMutation,
  useUpdateCommunityStatusMutation,
} from "@/services";
import { useQueryClient } from "@tanstack/react-query";
import { Building, Edit, Landmark, MoreVertical, ShieldCheck, ShieldOff, User2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "@/components/Toast";

const useCommunities = () => {
  const [globalFilter, setGlobalFilter] = useState("");
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [deletedRow] = useState<any>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  //get communities
  const { data: communitiesData, isLoading: isLoadingCommunities } =
    useGetCommunities({});

  const {
    data: communityDeleteState,
    isLoading: isLoadingCommunityDeleteState,
  } = useGetCommunityDeleteState({
    id: deletedRow?.communityId,
    queryParams: { enabled: !!deletedRow?.communityId },
  });

  const { mutate: deleteInfo, isPending: isDeletePending } =
    useDeleteCommunityMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["communities"],
        });
        navigate(`/${routes.admin}/${routes.adminConsole}/${routes.communities}`);
        toast.success("Community Deleted Successfully");
      },
    });

  const { mutate: goLiveInfo, isPending: isGoLivePending } =
    useGoLiveCommunityMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["communities"],
        });
        toast.success("Community Lived Successfully");
      },
    });

  const { mutate: updateCommunityInfo, isPending: isUpdatePending } =
    useUpdateCommunityStatusMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["communities"],
        });
        toast.success("Status Updated Successfully");
      },
    });

  // Define Table Columns
  const columns = [
    {
      accessorKey: "displayId",
      header: "ID",
      accessorFn: (row: any) => String(row.displayId),
    },
    {
      accessorKey: "name",
      header: "Name",
      accessorFn: (row: any) => String(row?.name || ""),
    },
    {
      accessorKey: "generalPhoneNo",
      header: (
        <div className=" w-full">
          General <br /> Contact Phone
        </div>
      ),
      accessorFn: (row: any) => String(row?.generalPhoneNo || ""),
    },
    {
      accessorKey: "emergencyPhoneNo",
      header: (
        <div className=" w-full">
          Emergency <br /> Contact Phone
        </div>
      ),
      accessorFn: (row: any) => String(row?.emergencyPhoneNo || ""),
    },
    {
      accessorKey: "city",
      header: "City",
      accessorFn: (row: any) => String(row?.city || ""),
    },
    {
      accessorKey: "state",
      header: "State",
      accessorFn: (row: any) => String(row?.state || ""),
    },
    {
      accessorKey: "totalProperties",
      header: "# of Properties",
      cell: ({ row }: any) => {
        return row.original.totalProperties ? (
          <div
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              dispatch(setCommunityData(row.original));
              navigate(`${row.original.communityId}/${routes.properties}`);
            }}
            className="text-blue-500 hover:text-blue-600 cursor-pointer px-3"
          >
            {String(row.original.totalProperties)}
          </div>
        ) : (
          <div className="px-3">{"0"}</div>
        );
      },
    },
    {
      accessorKey: "totalUsers",
      header: "# of Users",
      cell: ({ row }: any) => {
        return row?.original?.totalUsers ? (
          <div
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              dispatch(setCommunityData(row.original));
              navigate(`${row.original.communityId}/${routes.users}`);
            }}
            className="text-blue-500 hover:text-blue-600 cursor-pointer px-3"
          >
            {String(row?.original?.totalUsers)}
          </div>
        ) : (
          <div className="px-3">{"0"}</div>
        );
      },
    },
    {
      accessorKey: "live",
      header: "Status",
      cell: ({ row }: any) => {
        return (
          <Badge
            variant="outline"
            className={cn(
              row.original.live && row?.original?.active
                ? "border-0 bg-green-100 text-green-600"
                : "border-0 bg-gray-200 text-gray-600",
              "px-4 py-2"
            )}
          >
            {row?.original?.live && row?.original?.active ? "Active" : "Inactive"}
          </Badge>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: (row: any) => {
        return (
          <DropdownMenu>
            <DropdownMenuTrigger>
              <MoreVertical className="w-5 h-5 cursor-pointer" />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-white border-0 shadow-md"
            >
              <DropdownMenuItem
                className="flex items-center gap-2"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  dispatch(setCommunityData(row.row.original));
                  navigate(`${row.row.original.communityId}/edit`);
                }}
              >
                <Edit className="w-4 h-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                className="flex items-center gap-2"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  dispatch(setCommunityData(row.row.original));
                  navigate(
                    row.row.original.communityId + "/" + routes.properties
                  );
                }}
              >
                <Building className="w-4 h-4" />
                Manage property
              </DropdownMenuItem>
              <DropdownMenuSub>
                <DropdownMenuSubTrigger
                  className="gap-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                >
                  <Landmark className="h-4 w-4" /> Financials
                </DropdownMenuSubTrigger>

                <DropdownMenuPortal>
                  <DropdownMenuSubContent className="w-52">
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger
                        className="gap-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                        }}
                      >
                        Configuration
                      </DropdownMenuSubTrigger>

                      <DropdownMenuPortal>
                        <DropdownMenuSubContent className="w-52">
                          <DropdownMenuItem
                            className="flex items-center gap-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              dispatch(setCommunityData(row.row.original));
                              navigate(
                                row.row.original.communityId +
                                  "/" +
                                  routes.documentTypeConfig
                              );
                            }}
                          >
                            Document Type
                          </DropdownMenuItem>
                        </DropdownMenuSubContent>
                      </DropdownMenuPortal>
                    </DropdownMenuSub>
                    <DropdownMenuItem
                      className="flex items-center gap-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        dispatch(setCommunityData(row.row.original));
                        navigate(
                          row.row.original.communityId + "/" + routes.budgetMgmt
                        );
                      }}
                    >
                      Budget Management
                    </DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>
              <DropdownMenuItem
                className="flex items-center gap-2"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  dispatch(setCommunityData(row.row.original));
                  navigate(
                    row.row.original.communityId + "/" + routes.vendorMgmt
                  );
                }}
              >
                <User2 className="w-4 h-4" />
                Vendor Management
              </DropdownMenuItem>
              {row?.row?.original?.live ? <DropdownMenuItem
                className="flex items-center gap-2"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  updateCommunityInfo({communityId: row?.row?.original?.communityId, data: {active: !row?.row?.original?.active}})
                }}
                disabled={isUpdatePending}
              >
                {row?.row?.original?.active ? <>
                  <ShieldOff className="w-4 h-4" />
                  Inactive
                </> : <>
                  <ShieldCheck className="w-4 h-4" />
                  Active
                </>}
              </DropdownMenuItem> : <DropdownMenuItem
                className="flex items-center gap-2"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  goLiveInfo({communityId: row?.row?.original?.communityId})
                }}
                disabled={isGoLivePending}
              >
                <ShieldCheck className="w-4 h-4" />
                Go Live
              </DropdownMenuItem>}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  const onClose = () => {
    setOpenDialog(false);
  };

  const onConfirm = (reason: string) => {
    deleteInfo({ id: deletedRow?.communityId, reason });
    onClose();
  };

  const onAddCommunity = () => {
    navigate(`${routes.add}`);
  };

  const onRowClick = (row: any) => {
    dispatch(setCommunityData(row.original));
    navigate(`${row.original.communityId}/view`);
  };

  return {
    onAddCommunity,
    globalFilter,
    setGlobalFilter,
    columns,
    communitiesData,
    isLoadingCommunities,
    onClose,
    openDialog,
    isDeletePending,
    onConfirm,
    communityDeleteState,
    isLoadingCommunityDeleteState,
    deletedRow,
    onRowClick,
  };
};

export default useCommunities;
